import IconView from '@pe-libs/react-components/build/components/PeIcons/View.svg';
import PeLink from '@pe-libs/react-components/build/components/PeLink';
import PeTypography from '@pe-libs/react-components/build/components/PeTypography';
import { getPublicEnvironmentValue } from '@pe-libs/react-components/build/lib/environment';
import useEventTracker from '@pe-libs/react-components/build/lib/hooks/useEventTracker';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import img from '#assets/images/landing/default/thankYouPage/children.png';
import SmallFooter from '#components/SmallFooter';
import { selectIsEmployerHost } from '#modules/app/selectors/settings';
import { getProfilePublicData } from '#modules/defaultLandingReferral/selectors/profile';
import Header from '../Header';
import HeroImage from '../HeroImage';
import LandingForm from '../LandingForm';
import SocialButtons from '../SocialButtons';
import messages from './messages';
import useStyles from './styles';

type PropsType = {
  data: {
    attributes: {
      id: string;
      title: string;
      description?: string;
      offers?: string;
      profileUrl: string;
      avatarUrlFull: string;
      avatarUrlMedium: string;
      headerUrlFull: string;
      headerUrlMedium: string;
    };
  };
};

const Landing: FC<PropsType> = ({ data }) => {
  const formatMessage = useIntlMessage();
  const { asPath, query } = useRouter();
  const isEmployerHost = useSelector(selectIsEmployerHost);
  const link = `${getPublicEnvironmentValue('WWW_HOST', isEmployerHost)}${asPath}`;
  const profilePublicData = useSelector(getProfilePublicData);
  const { attributes } = data ?? profilePublicData;

  const profileTitle = attributes.title;
  const profileDescription = attributes.offers;
  const profileAbout = attributes.description;
  const profileId = data.attributes.id;
  const { profileUrl } = attributes;

  const { dispatchEvent } = useEventTracker();

  const { id: reviewId, email: emailAddress } = query;

  useEffect(() => {
    if (profileId) {
      dispatchEvent({
        from: 'referral-feature',
        type: 'referral-feature.page.visited',
        payload: {
          profileId,
          reviewId,
          emailAddress,
        },
      });
    }
  }, []);

  const profileLink = `${profileUrl}?itm_source=reflp&itm_medium=profile&itm_campaign=referral`;

  const getFontSize = () => {
    if (profileDescription && profileDescription.length <= 200) {
      return { desktop: '1.5rem', mobile: '1.125rem' };
    }

    if (profileDescription && profileDescription.length > 200 && profileDescription.length <= 1000) {
      return { desktop: '1.125rem', mobile: '1rem' };
    }

    return { desktop: '1rem', mobile: '0.875rem' };
  };

  const styles = useStyles({ fontSize: getFontSize() });

  return (
    <>
      <Head>
        <title>{formatMessage(messages.title)}</title>
        <meta name="description" content={formatMessage(messages.description, { profileName: profileTitle })} />
        <meta property="og:title" content={formatMessage(messages.title)} />
        <meta property="og:description" content={formatMessage(messages.description, { profileName: profileTitle })} />
        <meta property="og:image" content={img} />
        <meta name="robots" content="noindex" />
      </Head>
      <Header />
      <HeroImage
        avatar={data.attributes.avatarUrlFull}
        headerUrlMedium={data.attributes.headerUrlMedium}
        headerUrlFull={data.attributes.headerUrlFull}
      />
      <div className={styles.contentWrapper}>
        <PeTypography variant="subtitle1">
          <div className={styles.profileTitle}>
            <PeLink href={profileLink} target="_blank">
              {profileTitle}
              <IconView />
            </PeLink>
          </div>
          <div className={styles.profileDesc}>{profileDescription}</div>
          <div className={styles.socialTitle}>{formatMessage(messages.socialTitle)}</div>
          <SocialButtons link={link} />
        </PeTypography>
      </div>
      <div className={styles.goldBackground}>
        <div className={styles.contentContainer}>
          <div className={styles.profileAboutContainer}>
            <PeTypography variant="h3">
              {formatMessage(messages.profileAboutTitle, { company: profileTitle })}
            </PeTypography>
            <div className={styles.profileAbout}>
              <PeTypography variant="body1">{profileAbout}</PeTypography>
              <PeTypography variant="body1" className={styles.formInfo}>
                {formatMessage(messages.formInfo)}
              </PeTypography>
            </div>
          </div>
          <LandingForm profileTitle={profileTitle} />
        </div>
      </div>
      <SmallFooter />
    </>
  );
};

export default Landing;
